import { FC, useRef, useState } from 'react';

import { Box, Button } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import { UKBankDetails } from 'types/graphql';

import { showErrorToast } from 'src/components/Toasts/toast';
import businessInformationValidationSchema from 'src/constants/formValidations/addMerchantStep5';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useBankValidation } from 'src/hooks';
import useCountryHelpers from 'src/hooks/useCountryHelpers';
import { isIreland } from 'src/lib/countryConfig';

import { BusinessInformationForm } from '../../components/BusinessInformationForm';
import CardTurnoverForm from '../../components/CardTurnoverForm';
import GBBankDetailsForm from '../../components/GBBankDetailsForm/GBBankDetailsForm';
import GoodsAndServicesForm from '../../components/GoodsAndServicesForm';
import IEBankDetailsForm from '../../components/IEBankDetailsForm';

type BusinessInformationFormValues = {
    [key: string]: unknown;
};

interface BusinessInformationProps {
    taCode: string;
}

const BusinessInformation: FC<BusinessInformationProps> = ({ taCode }) => {
    const grid1Ref = useRef(null);
    const grid2Ref = useRef(null);
    const { getCountryName, getPostalLabel } = useCountryHelpers();

    const { addMerchant, merchantApplication, handleBack } = useAddMerchantContext();
    const { validateIBANAndBIC } = useBankValidation();

    const [UKBankDetailsResult, setUKBankDetailsResult] = useState<UKBankDetails>();
    const [validatingBankDetails, setValidatingBankDetails] = useState(false);

    const getInitialBankDetails = (legalCompanyName: string) => {
        if (isIreland) {
            return {
                settlementAccountBic: '',
                settlementAccountIban: '',
                settlementAccountAccountName: legalCompanyName ?? '',
                settlementAccountBankName: '',
                chargeableAccountBic: '',
                chargeableAccountIban: '',
                chargeableAccountAccountName: legalCompanyName ?? '',
                chargeableAccountBankName: '',
            };
        }

        return {
            settlementAccountSortCode: '',
            settlementAccountAccountNumber: '',
            settlementAccountAccountName: legalCompanyName ?? '',
            settlementAccountBankName: '',
            settlementAccountBranch: '',
            settlementAccountAddressLine1: '',
            settlementAccountAddressLine2: '',
            settlementAccountTown: '',
            settlementAccountPostCode: '',
            chargeableAccountSortCode: '',
            chargeableAccountAccountNumber: '',
            chargeableAccountAccountName: legalCompanyName ?? '',
            chargeableAccountBankName: '',
            chargeableAccountBranch: '',
            chargeableAccountAddressLine1: '',
            chargeableAccountAddressLine2: '',
            chargeableAccountTown: '',
            chargeableAccountPostCode: '',
        };
    };

    const initalValues = {
        // Business Information
        taCode: taCode ?? '',
        memberTaName: '',
        existingAcquirerRelationship: false,
        merchantNumber: '',
        membershipNumber: '',
        // Goods and Services
        goodsServicesOwned: false,
        acceptFullPayment: false,
        acceptDepositPayment: false,
        percentagePaymentPriorToDelivery: '',
        percentageDepositTaken: '',
        sizeOfDepositTaken: '',
        averageTimeFullPaymentTaken: '',
        averageTimeAdvancePaymentTaken: '',
        levyChargeForGuarantees: false,
        stocksHeldAnyAddress: false,
        percentageOfCardTurnover: '',
        levyChargeForMembership: false,
        averageLengthOfGuarantees: '',
        percentageOfCardTurnoverForMembership: '',
        percentageOfGoodsReturned: '',
        averageLengthOfMembership: '',
        costOfMembership: '',
        thirdPartyProviderName: '',
        averageTimeFullPaymentDeliveryTaken: '',
        guaranteesWarrantiesThirdParty: false,
        // Card Turnover
        creditCardTurnover: '',
        totalCardTurnover: '',
        averageCardTransaction: '',
        totalAnnualCompanyTurnover: '',
        percentageOfCompanyTurnover: '',
        percentageViaCard: '',
        // Bank Details
        settlementAccountSameAsChargeable: true,
        ...getInitialBankDetails(
            merchantApplication?.merchantInformation?.aboutBusiness?.legalCompanyName
        ),
        // Stock Address
        stockEircode: '',
        tradingAddress_addressLine1: '',
        tradingAddress_addressLine2: '',
        tradingAddress_townOrCity: '',
        tradingAddress_county: '',
        tradingAddress_country: getCountryName(
            merchantApplication?.partner?.countryDetails?.alpha2Code
        ),
        isStepCompleted: false,
        ...(merchantApplication?.businessInformation as Object),
    };

    const handleBusinessInformationSubmit = (
        addMerchant: (data: {
            businessInformation: BusinessInformationFormValues;
        }) => Promise<void>,
        validateIBANAndBIC: (
            iban: string,
            bic: string,
            ibanField: string,
            bicField: string
        ) => Promise<Record<string, string>>
    ) => {
        return async (
            values: BusinessInformationFormValues,
            { setErrors }: FormikHelpers<BusinessInformationFormValues>
        ) => {
            let formattedValues = values;
            if (isIreland) {
                formattedValues = {
                    ...values,
                    settlementAccountIban:
                        typeof values.settlementAccountIban === 'string'
                            ? values.settlementAccountIban.toUpperCase()
                            : values.settlementAccountIban,
                    settlementAccountBic:
                        typeof values.settlementAccountBic === 'string'
                            ? values.settlementAccountBic.toUpperCase()
                            : values.settlementAccountBic,
                    chargeableAccountIban:
                        typeof values.chargeableAccountIban === 'string'
                            ? values.chargeableAccountIban.toUpperCase()
                            : values.chargeableAccountIban,
                    chargeableAccountBic:
                        typeof values.chargeableAccountBic === 'string'
                            ? values.chargeableAccountBic.toUpperCase()
                            : values.chargeableAccountBic,
                };

                const errors = await validateIBANAndBIC(
                    formattedValues.settlementAccountIban as string,
                    formattedValues.settlementAccountBic as string,
                    'settlementAccountIban',
                    'settlementAccountBic'
                );

                if (Object.keys(errors).length > 0) {
                    setErrors(errors);
                    return;
                }

                if (!formattedValues.settlementAccountSameAsChargeable) {
                    const chargeableErrors = await validateIBANAndBIC(
                        formattedValues.chargeableAccountIban as string,
                        formattedValues.chargeableAccountBic as string,
                        'chargeableAccountIban',
                        'chargeableAccountBic'
                    );

                    if (Object.keys(chargeableErrors).length > 0) {
                        setErrors(chargeableErrors);
                        return;
                    }
                }
            } else {
                if (validatingBankDetails) {
                    showErrorToast('Still validating bank details, please wait...');
                    return;
                }

                switch (UKBankDetailsResult?.StatusInformation) {
                    case 'InvalidAccountNumber':
                        showErrorToast('Invalid account number');
                        return;
                    case 'UnknownSortCode':
                        showErrorToast('Invalid sort code');
                        return;
                }
            }

            await addMerchant({
                businessInformation: { ...formattedValues, isStepCompleted: true },
            });
        };
    };

    return (
        <Formik
            initialValues={initalValues}
            validationSchema={businessInformationValidationSchema(
                getPostalLabel(merchantApplication?.partner?.countryDetails?.alpha2Code)
            )}
            onSubmit={handleBusinessInformationSubmit(addMerchant, validateIBANAndBIC)}
        >
            {(formikProps) => {
                return (
                    <Form
                        style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
                        autoComplete="off"
                    >
                        <BusinessInformationForm {...formikProps} />

                        <Box ref={grid1Ref}>
                            <GoodsAndServicesForm {...formikProps} />
                        </Box>

                        <Box ref={grid2Ref}>
                            <CardTurnoverForm {...formikProps} />
                        </Box>

                        {isIreland ? (
                            <IEBankDetailsForm {...formikProps} />
                        ) : (
                            <GBBankDetailsForm
                                {...formikProps}
                                setUKBankDetailsResult={(res) => {
                                    setUKBankDetailsResult(res);
                                    setValidatingBankDetails(false);
                                }}
                                setValidatingBankDetails={setValidatingBankDetails}
                                bankValidationResult={UKBankDetailsResult}
                            />
                        )}

                        <Box display="flex" justifyContent="flex-end">
                            <Button
                                sx={{
                                    alignSelf: 'end',
                                    marginTop: '24px',
                                    marginRight: '22px',
                                }}
                                onClick={handleBack}
                                variant="back-button"
                            >
                                Back
                            </Button>

                            <Button
                                sx={{
                                    alignSelf: 'end',
                                    marginTop: '24px',
                                }}
                                type="button"
                                variant="main"
                                onClick={async () => {
                                    const errors = await formikProps.validateForm();
                                    const hasErrors = Object.keys(errors).length > 0;

                                    if (hasErrors) {
                                        const errorMessage = 'Please fill in required fields.';

                                        if (
                                            errors.percentagePaymentPriorToDelivery ||
                                            errors.percentageDepositTaken ||
                                            errors.sizeOfDepositTaken ||
                                            errors.percentageOfCardTurnoverForMembership ||
                                            errors.percentageOfCardTurnover ||
                                            errors.averageLengthOfGuarantees ||
                                            errors.percentageOfGoodsReturned ||
                                            errors.tradingAddress_addressLine1 ||
                                            errors.tradingAddress_townOrCity ||
                                            errors.tradingAddress_county ||
                                            errors.stockEircode ||
                                            errors.averageTimeAdvancePaymentTaken ||
                                            errors.averageTimeFullPaymentTaken ||
                                            errors.averageLengthOfMembership ||
                                            errors.costOfMembership
                                        ) {
                                            grid1Ref.current.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'start',
                                            });
                                        }

                                        if (
                                            errors.creditCardTurnover ||
                                            errors.totalCardTurnover ||
                                            errors.averageCardTransaction ||
                                            errors.percentageOfCardTurnoverForMembership ||
                                            errors.percentageOfCardTurnover ||
                                            errors.averageLengthOfGuarantees ||
                                            errors.percentageOfGoodsReturned ||
                                            errors.tradingAddress_addressLine1 ||
                                            errors
                                        ) {
                                            grid2Ref.current.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'start',
                                            });
                                        }
                                        showErrorToast(errorMessage);
                                        return;
                                    }

                                    if (!isIreland && validatingBankDetails) {
                                        showErrorToast(
                                            'Still validating bank details, please wait...'
                                        );
                                        return;
                                    }

                                    formikProps.submitForm();
                                }}
                            >
                                Next
                            </Button>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};
export default BusinessInformation;
